import type { ReactNode } from 'react';
import { isFilled, type LinkField } from '@prismicio/client';
import { PrismicNextLink } from '@prismicio/next';
import { ArrowRightIcon } from '@/assets';
import { cn } from '@/lib/utils';

/**
 * Editors sometimes type the arrow into the copy itself. The arrow is drawn by
 * the icon, so strip a leading or trailing glyph to avoid rendering two.
 */
export const stripArrowGlyph = (text: string): string =>
  text
    .replace(/^\s*(→|->)\s*/, '')
    .replace(/\s*(→|->)\s*$/, '')
    .trim();

type Props = {
  children: ReactNode;
  /** When filled, the whole line becomes a link. */
  link?: LinkField;
  /** Alignment and type scale are owned by the calling card. */
  className?: string;
  iconClassName?: string;
};

/**
 * An arrow glyph followed by arbitrary content, optionally wrapped in a link.
 * Rendered as a `div` rather than a `span` so a card can pass block content
 * (a rich-text paragraph) without invalid nesting.
 */
export function ArrowLine({ children, link, className, iconClassName }: Props) {
  const inner = (
    <div className={cn('inline-flex gap-2', className)}>
      <ArrowRightIcon className={cn('shrink-0', iconClassName)} />
      {children}
    </div>
  );

  if (link && isFilled.link(link)) {
    return (
      <PrismicNextLink field={link} className="group inline-flex">
        {inner}
      </PrismicNextLink>
    );
  }

  return inner;
}
